import { useCustom, useGetIdentity } from "@refinedev/core";
import { KeycloakIdentity } from "../providers/authProvider";
import { useEffect, useMemo } from "react";
import { Country, useMarketplaceStore } from "../store/useMarketplaceStore";
import { useTokenValidation } from "./useTokenValidation";

export type MarketplaceListResponse = {
  marketplaces: Country[];
};

export const useInitializeApp = () => {
  const { data: identity, isLoading: identityLoading } =
    useGetIdentity<KeycloakIdentity>();

  const hasSellerId = !!identity?.sellerId;

  const { data, isLoading: marketplaceLoading } =
    useCustom<MarketplaceListResponse>({
      url: "marketplace",
      method: "get",
      queryOptions: {
        enabled: hasSellerId,
        cacheTime: 0,
        queryKey: ["initialMarketplace"],
      },
    });

  const marketplaces = data?.data?.marketplaces ?? [];

  // Get marketplaces with stores only
  const marketplaceList = useMemo(() => {
    if (marketplaceLoading || marketplaces.length === 0) return [];

    return marketplaces.filter((marketplace) => !!marketplace.storeName);
  }, [marketplaceLoading, marketplaces]);

  // Set countries in global state
  const setCountries = useMarketplaceStore((state) => state.setCountries);
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  useEffect(() => {
    if (marketplaceList.length === 0) return;
    setCountries(marketplaceList);
  }, [marketplaceList]);

  // Validate token for the currently selected marketplace only
  const { tokenValidationState, invalidMarketplace, isValidating } =
    useTokenValidation(selectedCountry);

  // User has a store if the store list is not empty
  const hasStore = !marketplaceLoading && marketplaceList.length > 0;

  // Check if the selected marketplace token is invalid and user needs to re-authenticate
  const needsReAuthentication = hasStore && tokenValidationState === "invalid";

  const showLoading =
    identityLoading || (hasSellerId && marketplaceLoading) || isValidating;

  return {
    showLoading,
    hasStore,
    needsReAuthentication,
    invalidMarketplace,
  };
};
